import { useEffect, useMemo } from "react";
import { Redirect, Route } from "react-router-dom";
import { CookiesKey } from "src/globals";
import { getCookie } from "src/globals/cookie";
import { useUserContext } from "src/globals/providers";
import { User } from "src/sdk";

interface AuthLayoutProps {
  component: any;
  layout: any;
  isAuthProtected: boolean;
  isAdminLayout: boolean;
  path?: string;
  exact?: boolean;
  key?: number;
}

const Authmiddleware = ({
  component,
  layout,
  isAuthProtected,
  isAdminLayout,
  path,
  ...rest
}: AuthLayoutProps) => {
  const Layout = layout;
  const Component = component;

  const { user, setUser } = useUserContext();

  const token = getCookie(CookiesKey.JWT_TOKEN);
  const userJsonValues = getCookie(CookiesKey.USER_COOKIE);
  const isLoggedIn = !!token;

  // Parse cookie user once (safe)
  const cookieUser = useMemo<User | null>(() => {
    if (!userJsonValues) return null;
    try {
      return JSON.parse(userJsonValues) as User;
    } catch {
      return null;
    }
  }, [userJsonValues]);

  // Use context user if present, otherwise fall back to cookieUser
  const effectiveUser = user ?? cookieUser;

  // Hydrate context user (nice to keep the app consistent)
  useEffect(() => {
    if (!user && cookieUser && setUser) {
      setUser(cookieUser);
    }
  }, [user, cookieUser, setUser]);

  const roles = effectiveUser?.roles ?? [];
  const isSuperAdmin = roles.includes("SuperAdministrator");
  const isAdmin = roles.includes("Administrator");
  const isAdminUser = isSuperAdmin || isAdmin;

  // Optional pages that should not force a redirect
  const allowInAdminLayoutEvenIfNotAdmin =
    path === "/logout" || path === "/changePassword";

  return (
    <Route
      {...rest}
      render={(props) => {
        const fromState = { from: props.location };

        // 1) Protected route but not logged in -> login
        if (isAuthProtected && !isLoggedIn) {
          return <Redirect to={{ pathname: "/login", state: fromState }} />;
        }

        // 2) Public route but already logged in -> DEFAULT LANDING
        // You want User+Admin to land in USER area first.
        if (!isAuthProtected && isLoggedIn) {
          if (isSuperAdmin) {
            return (
              <Redirect to={{ pathname: "/admin-ticketing", state: fromState }} />
            );
          }

          // Everyone else (including User+Administrator) goes to user first
          return <Redirect to={{ pathname: "/ticketing", state: fromState }} />;
        }

        // 3) Admin layout protection: only Admin/SuperAdmin can enter admin pages
        // IMPORTANT: use effectiveUser (cookie fallback) to avoid race redirect.
        if (
          isAuthProtected &&
          isLoggedIn &&
          isAdminLayout &&
          !isAdminUser &&
          !allowInAdminLayoutEvenIfNotAdmin
        ) {
          return <Redirect to={{ pathname: "/ticketing", state: fromState }} />;
        }

        return (
          <Layout>
            <Component {...props} />
          </Layout>
        );
      }}
    />
  );
};

export default Authmiddleware;
